import React, { useEffect, useState } from 'react';

import { Button } from 'react-covideo-common';
import styled from 'styled-components/macro';
import { RECORDING_STATUS } from './types';
import { useImageSegmenter } from './useImageSegmenter';
import { VirtualBackgrounds } from './VirtualBackgrounds';
import { RecordCamLoader } from './RecordCamLoader';
import { CovideoIcon } from 'components/CovideoIcon';

const VirtualBackgroundsWrapper = styled.div`
  max-width: 770px;
  margin-top: 25px;
`;

const VirtualBackgroundButtonWrapper = styled.div`
  position: absolute;
  bottom: 25px;
  right: 20px;
  z-index: 150;
`;

type VirtualBackgroundTypes = 'none' | 'default' | 'custom';
type PrivacyTypes = 'PUBLIC' | 'PRIVATE' | 'TEAM';

type VirtualBackground = {
  url: string; // required in case of custom backgrounds
  type: VirtualBackgroundTypes;
  index: number; // required in case of default backgrounds
  privacy: PrivacyTypes; // required in case of custom backgrounds
  id: string; // required in case of custom backgrounds
};

interface IVirtualBackgroundButtonProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isSegmenterActive: boolean;
  showVirtualBackgrounds: boolean;
  setIsSegmenterActive: React.Dispatch<React.SetStateAction<boolean>>;
  setShowVirtualBackgrounds: React.Dispatch<React.SetStateAction<boolean>>;
  status: RECORDING_STATUS;
}
const initialVirtualBgState: VirtualBackground = {
  url: '',
  type: 'none',
  index: -1,
  privacy: 'PUBLIC',
  id: '',
};

const getVirtualBackgroundFromStorage = (): VirtualBackground => {
  const vbSelection = localStorage.getItem('covideoVirtualBackground') || '';
  try {
    const savedVirtualBackground: VirtualBackground = JSON.parse(vbSelection);
    if (savedVirtualBackground && typeof savedVirtualBackground === 'object') {
      return savedVirtualBackground;
    }
    return initialVirtualBgState;
  } catch (ex) {
    return initialVirtualBgState;
  }
};
const saveVirtualBackgroundToStorage = (
  currentVirtualBackground: VirtualBackground
) => {
  localStorage.setItem(
    'covideoVirtualBackground',
    JSON.stringify(currentVirtualBackground)
  );
};

export const VirtualBackgroundsButton = ({
  videoRef,
  canvasRef,
  isSegmenterActive,
  setIsSegmenterActive,
  status,
  showVirtualBackgrounds,
  setShowVirtualBackgrounds,
}: IVirtualBackgroundButtonProps) => {
  const initialImage = getVirtualBackgroundFromStorage();

  const [selectedVirtualBackground, setSelectedVirtualBackground] =
    useState<VirtualBackground>(initialImage);
  const [isLoading, setIsLoading] = useState(!!initialImage.url);
  const {
    onCreateVirtualBackgroundHandler,
    onDestroyVirtualBackgroundHandler,
    imageRef,
    getCavansBgImage,
  } = useImageSegmenter(canvasRef, videoRef, setIsLoading);

  const startVirualBg = () => {
    if (
      !!canvasRef.current?.width &&
      !!canvasRef.current?.height &&
      !!videoRef.current?.videoWidth &&
      !!videoRef.current.videoHeight
    ) {
      onCreateVirtualBackgroundHandler();
      setIsSegmenterActive(true);
    }
  };

  const stopVirtualBg = () => {
    onDestroyVirtualBackgroundHandler();
    setIsSegmenterActive(false);
  };

  const onChangeImageHandler = (url: string, getCavansBgImage?: () => void) => {
    if (imageRef.current) {
      const imageElement = imageRef.current;
      imageElement.src = `${url}?t=virtual`;
      imageElement.crossOrigin = 'Anonymous';

      const handleImageLoad = () => {
        getCavansBgImage?.();
        if (!isSegmenterActive) {
          startVirualBg(); // Make sure this is spelled correctly
        }
      };

      // Assign the onload handler
      imageElement.onload = handleImageLoad;

      // Cleanup function to remove the onload event handler
      return () => {
        imageElement.onload = null;
      };
    }
  };

  const onSelectImageSaveToLocalStorageHandler = ({
    url,
    type,
    index,
    privacy,
    id,
  }: VirtualBackground) => {
    const saveParams = { url, type, index, privacy, id };
    setSelectedVirtualBackground(saveParams);
    saveVirtualBackgroundToStorage(saveParams);
  };

  const onVirtualBacgroundClickHandler = () => {
    setShowVirtualBackgrounds(!showVirtualBackgrounds);
  };

  useEffect(() => {
    if (status === RECORDING_STATUS.CAMERA_READY) {
      const { url } = selectedVirtualBackground;
      if (!!url && imageRef.current) {
        const imageElement = imageRef.current;
        imageElement.crossOrigin = 'Anonymous';
        imageElement.src = `${url}?t=virtual`;

        imageElement.onload = async () => {
          if (!isSegmenterActive) {
            getCavansBgImage?.();
            startVirualBg();
          }
        };

        // Cleanup function to remove the onload event handler
        return () => {
          imageElement.onload = null;
        };
      }
    }
  }, [status]);

  useEffect(() => {
    return () => {
      onDestroyVirtualBackgroundHandler();
    };
  }, []);

  return (
    <>
      {!showVirtualBackgrounds && (
        <>
          {isLoading && <RecordCamLoader text='Applying Virtual Background' />}
          {status === RECORDING_STATUS.CAMERA_READY && (
            <>
              {!isLoading && (
                <VirtualBackgroundButtonWrapper>
                  <Button
                    icon={<CovideoIcon name='virtual-bg' height={'16px'} />}
                    variant='secondary'
                    text='Virtual Backgrounds'
                    onClick={onVirtualBacgroundClickHandler}
                  />
                </VirtualBackgroundButtonWrapper>
              )}
            </>
          )}
        </>
      )}
      <>
        <VirtualBackgroundsWrapper
          hidden={
            !showVirtualBackgrounds || status === RECORDING_STATUS.RECORDING
          }
        >
          <VirtualBackgrounds
            selectedVirtualBackgroundUrl={selectedVirtualBackground.url}
            onMainButtonClick={() => {
              setShowVirtualBackgrounds(false);
            }}
            onSelect={(
              url: string = '',
              type: VirtualBackgroundTypes,
              index: number,
              privacy: PrivacyTypes = 'PUBLIC',
              id: string = ''
            ) => {
              const saveParams = {
                url,
                type,
                index,
                privacy,
                id,
              };
              if (type === 'none') {
                stopVirtualBg();
                onSelectImageSaveToLocalStorageHandler(saveParams);
                return;
              }
              onSelectImageSaveToLocalStorageHandler(saveParams);
              onChangeImageHandler(url, getCavansBgImage);
            }}
          />
        </VirtualBackgroundsWrapper>
      </>

      <img
        ref={imageRef}
        crossOrigin='anonymous'
        alt='Segmentation'
        style={{ display: 'none' }}
      />
    </>
  );
};
